import { t } from './lib/i18n';
import { useEffect, useState } from 'react';
import { SessionGame } from './SessionGame';
import {
  cancelSomeoneNew, findSomeone, loadMatchmaking, rememberMatchmakingActive, watchMatchmaking,
  type MatchmakingSnapshot, type PairInteraction
} from './lib/matchmaking-core';
import { rememberActiveSession } from './lib/session-core';
import { RecoveryNotice } from './RecoveryNotice';
import { useFreshMoment } from './lib/experience';
import { haptics } from './lib/haptics';
import { trackAnalytics } from './lib/analytics';

const interactionName = (interaction: PairInteraction): string => interaction === 'same_brain' ? t('SAME BRAIN') : interaction === 'read_me' ? t('READ ME') : t("DON'T MATCH");

export function SomeoneNewExperience({ initData, userId, onHome, onFriend }: {
  initData: string; userId: string; onHome: () => void; onFriend: () => void;
}) {
  const [selected, setSelected] = useState<PairInteraction | null>(null);
  const [snapshot, setSnapshot] = useState<MatchmakingSnapshot | null>(null);
  const [connected, setConnected] = useState(true);
  const [enteredMatch, setEnteredMatch] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const foundKey = snapshot?.match?.sessionId ?? null;
  const freshFound = useFreshMoment(foundKey, haptics.bothLocked);

  useEffect(() => {
    let cancelled = false;
    rememberMatchmakingActive(true);
    void loadMatchmaking(initData).then((next) => {
      if (!cancelled) {
        setSnapshot(next);
        if (next.matchmaking.interactionType) setSelected(next.matchmaking.interactionType);
      }
    }).catch((reason: unknown) => {
      if (!cancelled) setError(reason instanceof Error ? reason.message : t('Could not restore matchmaking.'));
    });
    const dispose = watchMatchmaking({
      initData, onConnection: setConnected,
      onSnapshot: (next) => {
        setSnapshot((current) => !current || next.matchmaking.revision >= current.matchmaking.revision ? next : current);
        if (next.matchmaking.interactionType) setSelected(next.matchmaking.interactionType);
        if (next.match) rememberActiveSession(next.match.sessionId);
      }
    });
    return () => { cancelled = true; dispose(); };
  }, [initData]);

  useEffect(() => {
    if (snapshot?.match) rememberActiveSession(snapshot.match.sessionId);
  }, [snapshot?.match]);

  useEffect(() => {
    if (snapshot?.matchmaking.status !== 'waiting' || !snapshot.matchmaking.expiresAt) return;
    const delay = Math.max(0, Math.min(2_147_000_000, new Date(snapshot.matchmaking.expiresAt).getTime() - Date.now() + 250));
    const timer = window.setTimeout(() => void loadMatchmaking(initData).then(setSnapshot).catch(() => undefined), delay);
    return () => window.clearTimeout(timer);
  }, [snapshot?.matchmaking.status, snapshot?.matchmaking.expiresAt, initData]);

  const search = async (): Promise<void> => {
    if (!selected) return;
    setBusy(true); setError(null);
    try { setSnapshot(await findSomeone(initData, selected)); }
    catch (reason) { setError(reason instanceof Error ? reason.message : t('Could not search right now.')); }
    finally { setBusy(false); }
  };

  const leave = async (destination: 'home' | 'friend'): Promise<void> => {
    setBusy(true); setError(null);
    try { await cancelSomeoneNew(initData); }
    catch (reason) { setError(reason instanceof Error ? reason.message : t('Could not leave the queue yet.')); setBusy(false); return; }
    rememberMatchmakingActive(false);
    setBusy(false);
    if (destination === 'friend') onFriend(); else onHome();
  };

  if (snapshot?.match && enteredMatch) {
    return <SessionGame sessionId={snapshot.match.sessionId} initData={initData} userId={userId} onHome={(createChallenge = false) => { rememberMatchmakingActive(false); if (createChallenge) onFriend(); else onHome(); }} />;
  }

  if (snapshot?.match) {
    return (
      <main className={freshFound ? 'app-shell challenge-screen stranger-found energy-expressive fresh-social-arrival' : 'app-shell challenge-screen stranger-found energy-expressive'}>
        <div className="brand-mark">UTAGE</div>
        <section className="challenge-card">
          <p className="eyebrow">{t('FOUND SOMEONE.')}</p>
          <div className="stranger-avatar" aria-hidden="true">●</div>
          <h1>{snapshot.match.person.firstName}</h1>
          <p>{selected ? interactionName(selected) : 'UTAGE'} · {t('10 questions')}</p>
          <button className="primary-action" onClick={() => { trackAnalytics('play_selected', { destination: 'matched_stranger_session' }); rememberMatchmakingActive(false); setEnteredMatch(true); }}>{t('PLAY')}</button>
        </section>
      </main>
    );
  }

  if (snapshot?.matchmaking.status === 'waiting' || snapshot?.matchmaking.status === 'expired') {
    const expired = snapshot.matchmaking.status === 'expired';
    return (
      <main className="app-shell challenge-screen stranger-waiting energy-quiet">
        <div className="brand-mark">UTAGE</div>
        <RecoveryNotice interrupted={!connected} preserved="YOUR PLACE IS HELD" />
        <section className="challenge-card">
          <p className="eyebrow">{t('SOMEONE NEW')} · {selected ? interactionName(selected) : t('PAIR')}</p>
          <div className="waiting-pulse" />
          <h1>{expired ? t('No one was ready yet.') : t('Looking for one real person…')}</h1>
          <p>{expired ? t('You can keep waiting or try the search again.') : t('No fake counts. No ETA. We’ll update this only when a real person is ready.')}</p>
          <button className="primary-action" disabled={busy} onClick={() => void search()}>{expired ? t('CONTINUE WAITING') : t('KEEP WAITING')}</button>
          <button className="secondary-wide" disabled={busy} onClick={() => void search()}>{t('RETRY SEARCH')}</button>
          <button className="text-action" disabled={busy} onClick={() => void leave('friend')}>{t('CHALLENGE A FRIEND')}</button>
          <button className="text-action" disabled={busy} onClick={() => void leave('home')}>{t('HOME')}</button>
          {error ? <p className="inline-error" role="alert">{error}</p> : null}
        </section>
      </main>
    );
  }

  return (
    <main className="app-shell home-screen interaction-screen energy-calm">
      <header className="home-header"><div className="brand-mark">UTAGE</div></header>
      <section className="interaction-heading"><p className="section-label">{t('SOMEONE NEW')}</p><h1>{t('FIND SOMEONE')}</h1><p className="quiet-copy">{t('Choose an interaction. The game introduces the human.')}</p></section>
      <section className="context-picker">
        <div className="stranger-mode-row" role="group" aria-label={t('Choose an interaction')}>
          {(['same_brain', 'read_me', 'dont_match'] as const).map((interaction) => <button key={interaction} className={selected === interaction ? 'mode-chip selected' : 'mode-chip'} onClick={() => { trackAnalytics('interaction_selected', { contextType: 'stranger', interactionType: interaction }); setSelected(interaction); }}>{interactionName(interaction)}</button>)}
        </div>
        <button className="primary-action" disabled={!selected || busy} onClick={() => void search()}>{busy ? t('SEARCHING…') : t('FIND SOMEONE')}</button>
        <button className="text-action" disabled={busy} onClick={() => void leave('home')}>{t('HOME')}</button>
        {error ? <p className="inline-error" role="alert">{error}</p> : null}
      </section>
    </main>
  );
}
